/**
 * Attendance/timesheet Excel export.
 *
 * Reuses the SAME per-day status pipeline as the timesheet views
 * (`statusTokens.buildMonthDaySheet`), so exported statuses match the screen:
 * one status per day (present/late/remote > leave > holiday > weekend),
 * absent only on past working days, future days excluded.
 *
 * Workbook:
 *   "Summary" — one row per employee (monthly) or employee-month (yearly)
 *               with per-status day counts + worked/shift hours.
 *   "Daily"   — one row per employee-day with status, leave type/holiday,
 *               in/out, duration and remote flag.
 *
 * Public API:
 *   exportTimesheetXlsx({ period, year, month, email, branch,
 *                         onProgress, onError, onSuccess })
 *     period: 'monthly' (all/filtered employees) | 'yearly' (one employee)
 */
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

import { apiClient } from '@libs/apiRequest';
import { getCookie } from '@libs/cookies';
import { AUTH_TOKEN_KEY } from 'auth/common';
import {
    calculateSecondsToHoursAndMinutes,
    formatTime
} from '@libs/dateTime';

import { buildMonthDaySheet, summarizeDays } from '../statusTokens';

const PAGE_SIZE = 25;
const MAX_PAGES = 100;

const authHeaders = () => {
    const token = getCookie(AUTH_TOKEN_KEY);
    return token
        ? { headers: { Authorization: `Bearer ${token}` } }
        : undefined;
};

/* ------------------------------------------------------------------ */
/* Data fetch                                                          */
/* ------------------------------------------------------------------ */
const fetchMonthlyAllEmployees = async (
    { year, month, email, branch },
    onProgress
) => {
    const users = [];
    for (let page = 1; page <= MAX_PAGES; page += 1) {
        const { data } = await apiClient.get(
            'attendance/get-all-monthly-attendances',
            {
                params: {
                    year,
                    month,
                    pageNumber: page,
                    pageSize: PAGE_SIZE,
                    ...(email ? { email } : {}),
                    ...(branch ? { filters: { branch } } : {})
                },
                ...(authHeaders() || {})
            }
        );
        const batch = data?.data || [];
        users.push(...batch);
        const totalPages = data?.totalPages || 1;
        onProgress &&
            onProgress(
                `Fetching employees page ${Math.min(page, totalPages)}/${totalPages}…`
            );
        if (page >= totalPages || !batch.length) break;
    }
    return users;
};

const fetchYearlyEmployee = async ({ year, email }) => {
    const { data } = await apiClient.get('attendance/get-yearly-attendance', {
        params: { year, email, pageNumber: 1, pageSize: 1 },
        ...(authHeaders() || {})
    });
    return data?.data || [];
};

/* ------------------------------------------------------------------ */
/* Sheet building                                                      */
/* ------------------------------------------------------------------ */
const buildSheet = (headers, rows) => {
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = headers.map((header, c) => {
        let wch = String(header).length;
        rows.forEach((row) => {
            const v = row[c];
            if (v !== undefined && v !== null)
                wch = Math.max(wch, String(v).length);
        });
        return { wch: Math.min(40, Math.max(9, wch + 2)) };
    });
    ws['!autofilter'] = {
        ref: XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: rows.length, c: Math.max(0, headers.length - 1) }
        })
    };
    return ws;
};

const SUMMARY_HEADERS = [
    'Employee',
    'E-Mail',
    'Period',
    'Working Days',
    'Total Present',
    'On site',
    'Remote',
    'Late',
    'Late (On site)',
    'Late (Remote)',
    'Casual Leave',
    'Sick Leave',
    'Maternity/Paternity Leave',
    'Compensatory Leave',
    'Other Leave',
    'Total Leave',
    'Holiday',
    'Weekend',
    'Absent',
    'Worked (h)',
    'Shift (h)'
];

const DAILY_HEADERS = [
    'Employee',
    'E-Mail',
    'Date',
    'Day',
    'Status',
    'Details',
    'In Time',
    'Out Time',
    'Duration (h)',
    'Remote'
];

const summaryRow = (user, periodLabel, summary, worked, shift) => [
    user.name || '',
    user.email || '',
    periodLabel,
    summary.workingDays,
    summary.totalPresent,
    summary.present,
    summary.remote,
    summary.arrivedLate,
    summary.lateOnsite,
    summary.lateRemote,
    summary.casualLeave,
    summary.sickLeave,
    summary.maternityPaternityLeave,
    summary.compensatoryLeave,
    summary.otherLeave,
    summary.leave,
    summary.holiday,
    summary.weekend,
    summary.absent,
    calculateSecondsToHoursAndMinutes(worked || 0),
    calculateSecondsToHoursAndMinutes(shift || 0)
];

const boundsOf = (user) => ({
    joiningDate: user.joiningDate,
    resignationDate: user.resignationDate
});

const dailyRows = (user, daySheet) =>
    daySheet
        .filter(
            (day) => day.status !== 'future' && day.status !== 'notEmployed'
        )
        .map((day) => [
            user.name || '',
            user.email || '',
            day.isoDate,
            dayjs(day.isoDate).format('ddd'),
            day.statusLabel,
            day.status === 'leave'
                ? day.leaveType || ''
                : day.status === 'holiday'
                ? day.label || ''
                : '',
            day.inTime ? formatTime(day.inTime) : '',
            day.outTime ? formatTime(day.outTime) : '',
            day.duration
                ? calculateSecondsToHoursAndMinutes(day.duration)
                : '',
            day.isFromRemote ? 'Yes' : ''
        ]);

/* ------------------------------------------------------------------ */
/* Public orchestrator                                                 */
/* ------------------------------------------------------------------ */
export async function exportTimesheetXlsx({
    period = 'monthly',
    year,
    month,
    email = '',
    branch = '',
    onProgress,
    onError,
    onSuccess
} = {}) {
    const progress = (message) => onProgress && onProgress(message);

    try {
        const summaryRows = [];
        const allDailyRows = [];

        if (period === 'yearly') {
            if (!email)
                throw new Error(
                    'Please provide an employee email for a yearly export.'
                );
            progress('Fetching yearly attendance…');
            const users = await fetchYearlyEmployee({ year, email });
            if (!users.length)
                throw new Error('No attendance found for that employee.');
            const user = users[0];

            for (let m = 1; m <= 12; m += 1) {
                const daySheet = buildMonthDaySheet(
                    user.entries || [],
                    `${year}-${m}`,
                    boundsOf(user)
                );
                const summary = summarizeDays(daySheet);
                const totals = (user.monthlyTotals || {})[m - 1] || {};
                summaryRows.push(
                    summaryRow(
                        user,
                        dayjs(`${year}-${m}-01`).format('MMM YYYY'),
                        summary,
                        totals.totalDuration,
                        totals.totalShiftDuration
                    )
                );
                allDailyRows.push(...dailyRows(user, daySheet));
                progress(`Preparing months ${m}/12…`);
            }
        } else {
            progress('Fetching employees…');
            const users = await fetchMonthlyAllEmployees(
                { year, month, email, branch },
                progress
            );
            if (!users.length)
                throw new Error('No employees match the selected filters.');

            const periodLabel = dayjs(`${year}-${month}-01`).format(
                'MMM YYYY'
            );
            users.forEach((user, index) => {
                const daySheet = buildMonthDaySheet(
                    user.entries || [],
                    `${year}-${month}`,
                    boundsOf(user)
                );
                const summary = summarizeDays(daySheet);
                summaryRows.push(
                    summaryRow(
                        user,
                        periodLabel,
                        summary,
                        user.totalDuration,
                        user.totalShiftDuration
                    )
                );
                allDailyRows.push(...dailyRows(user, daySheet));
                if (index % 10 === 9)
                    progress(
                        `Preparing employees ${index + 1}/${users.length}…`
                    );
            });
        }

        progress('Building Excel workbook…');
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
            wb,
            buildSheet(SUMMARY_HEADERS, summaryRows),
            'Summary'
        );
        XLSX.utils.book_append_sheet(
            wb,
            buildSheet(DAILY_HEADERS, allDailyRows),
            'Daily'
        );

        const nameParts = [
            'attendance',
            year,
            period === 'monthly' ? String(month).padStart(2, '0') : null,
            period === 'yearly' ? email.split('@')[0] : null
        ].filter(Boolean);
        XLSX.writeFile(wb, `${nameParts.join('-')}.xlsx`);
        onSuccess && onSuccess();
    } catch (err) {
        const msg =
            err?.response?.data?.message ||
            err?.message ||
            'Failed to generate the attendance Excel file.';
        onError && onError(msg);
    }
}

export default exportTimesheetXlsx;
